import { FC } from 'react';
import { menuController } from '@ionic/core/components';
import { IonLabel, IonTabBar, IonTabButton } from '@ionic/react';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { useMemo } from 'react';
import { InnerhiveSVGIcon } from '../theme/Icons';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { useSubscription } from '@meraki-internal/state';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { createUseStyles } from 'react-jss';
import { mediaMaxWidth, mediaMinWidth } from '../theme/utils';
import { IntercomState } from '../support/IntercomService';
import { ReminderBadge } from '../components/ReminderBadge';
import { SIDE_NAV_WIDTH } from './NavConstants';
import { useBreakpoints } from '../theme/useBreakpoints';
import { RecordButton } from '../appointments/recording/RecordButton';
import { useCanRecord } from '../appointments/recording/Recorder';

const useStyles = createUseStyles({
    tabBar: {
        [mediaMinWidth.lg]: {
            position: 'absolute',
            height: '100%',
            float: 'left',
            width: SIDE_NAV_WIDTH,
            gap: 24,
            top: 0,
            paddingTop: 32,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
        }
    },

    unreadBadge: {
        position: 'absolute',
        top: 42,
        left: 66,
        zIndex: 9999,

        // no hamburger in bottom nav
        [mediaMaxWidth.lg]: {
            display: 'none'
        }
    },

    selected: {},

    navItem: {
        fontSize: 11,
        fontWeight: 500,
        border: '3px solid transparent',
        '--ripple-color': 'transparent',
        '--padding-start': 0,
        '--padding-end': 0,

        '& > svg': {
            marginTop: 5
        },

        [mediaMinWidth.lg]: {
            flex: 0,
            width: '100%',

            '&::part(native)': {
                padding: '12px 0'
            }
        },

        '&:not($selected)': {
            '& svg': {
                fill: 'var(--ion-color-medium)'
            }
        },

        '&$selected': {
            color: 'var(--ion-color-primary)',

            '& svg': {
                fill: 'var(--ion-color-primary)'
            },

            [mediaMaxWidth.lg]: {
                borderTop: '3px solid var(--ion-color-primary)'
            },

            [mediaMinWidth.lg]: {
                borderRight: '3px solid var(--ion-color-primary)'
            }
        }
    }
});

interface INavConfig {
    id: string;
    icon: React.ReactNode;
    label: string;
    href?: string;
    onClick?: () => void;
    isSelected: boolean;
    isDisabled: boolean;
    isHidden?: boolean;
};

export const TabNavigation: FC = () => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const history = useInstance(HistoryViewModel);
    useSubscription(() => history);

    const mixpanel = useInstance(MixPanelEventEmitter);

    const intercomState = useInstance(IntercomState);
    useSubscription(() => intercomState);

    // NOTE: We avoid using careMapState.hasPermissionTo('put') here, because it relies on _activeCareMap,
    // which gets briefly unset during navigation, causing the quick add button to flicker
    const careMap = careMapState.getMostRecentCaremap();
    const isViewer = careMap && !careMap?.links?.self?.actions.includes('put');

    const canRecord = useCanRecord();

    const { isAboveBreakpoint } = useBreakpoints();
    const hasSideNav = isAboveBreakpoint('lg');

    const currentPath = history.getCurrentLocation().pathname;

    const nav = useMemo<INavConfig[]>(() => {
        return [
            {
                id: 'menu',
                label: '',
                icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" viewBox="0 0 448 512"><path fill="currentColor" d="M0 96C0 78.3 14.3 64 32 64l384 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 128C14.3 128 0 113.7 0 96zM0 256c0-17.7 14.3-32 32-32l384 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 288c-17.7 0-32-14.3-32-32zM448 416c0 17.7-14.3 32-32 32L32 448c-17.7 0-32-14.3-32-32s14.3-32 32-32l384 0c17.7 0 32 14.3 32 32z"></path></svg>,
                onClick: () => menuController.open('navigation-menu'),
                isSelected: false,
                isDisabled: false,
                isHidden: !hasSideNav
            },
            {
                id: 'caregiver',
                label: `Home`,
                icon: <svg xmlns="http://www.w3.org/2000/svg" width="25" viewBox="0 0 576 512"><path opacity=".4" d="M64 270.5c74.7-65.3 149.3-130.6 224-196L512.1 270.6l.4 201.3c0 22.1-17.9 40.1-40 40.1L392 512c-22.1 0-40-17.9-40-40l0-88.3c0-17.7-14.3-32-32-32l-64 0c-17.7 0-32 14.3-32 32l0 88.3c0 22.1-17.9 40-40 40l-79.9 0c-22.1 0-40-17.9-40-40L64 270.5z"/><path d="M266.9 7.9C279-2.6 297-2.6 309.1 7.9l256 224c13.3 11.6 14.6 31.9 3 45.2s-31.9 14.6-45.2 3L288 74.5 53.1 280.1c-13.3 11.6-33.5 10.3-45.2-3s-10.3-33.5 3-45.2l256-224z"/></svg>,
                href: '/caregiver',
                isSelected: currentPath === '/caregiver',
                isDisabled: false
            },
            {
                id: 'care',
                label: `Care`,
                icon: InnerhiveSVGIcon,
                href: '/care-redirect',
                onClick: () => {
                    mixpanel.track('Care Page Opened', () => ({
                        'Opened From': 'Menu'
                    }));
                },
                isSelected: currentPath.startsWith('/care/') || currentPath.startsWith('/caremaps/') || currentPath.startsWith('/notes/'),
                isDisabled: careMapState.getCareMaps().length === 0
            },
            {
                id: 'spacer',
                label: '',
                icon: '',
                isSelected: false,
                isDisabled: false,
                isHidden: hasSideNav || isViewer
            },
            {
                id: 'appointments',
                label: `Calendar`,
                icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" viewBox="0 0 448 512"><path opacity=".4" d="M0 192l448 0 0 272c0 26.5-21.5 48-48 48L48 512c-26.5 0-48-21.5-48-48L0 192zm64 80l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zm0 128l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zM192 272l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zm0 128l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zM320 272l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zm0 128l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16z"/><path d="M160 32c0-17.7-14.3-32-32-32S96 14.3 96 32l0 32L48 64C21.5 64 0 85.5 0 112l0 80 448 0 0-80c0-26.5-21.5-48-48-48l-48 0 0-32c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 32L160 64l0-32z"/></svg>,
                href: '/calendar-redirect',
                onClick: () => {
                    mixpanel.track('Appointments Page Opened', () => ({
                        'Opened From': 'Menu'
                    }));
                },
                isSelected: currentPath.startsWith('/calendar/'),
                isDisabled: false
            },
            {
                id: 'community',
                label: `Community`,
                icon: <svg xmlns="http://www.w3.org/2000/svg" width="28" viewBox="0 0 640 512"><path opacity=".4" d="M0 304c0 44.7 26.2 83.2 64 101.2L64 448c0 17.7 14.3 32 32 32l64 0c17.7 0 32-14.3 32-32l0-42.8 0-10.7c-20-24.7-32-56.2-32-90.5c0-37.8 14.6-72.2 38.4-97.9c-16.1-9-34.7-14.1-54.4-14.1l-32 0C50.1 192 0 242.1 0 304zm48 0c0-16.2 6-31.1 16-42.3l0 84.7C54 335 48 320.2 48 304zM64 96a64 64 0 1 0 128 0A64 64 0 1 0 64 96zM441.6 206.1C465.4 231.8 480 266.2 480 304c0 34.3-12 65.8-32 90.5l0 10.7 0 42.8c0 17.7 14.3 32 32 32l64 0c17.7 0 32-14.3 32-32l0-42.8c37.8-18 64-56.5 64-101.2c0-61.9-50.1-112-112-112l-32 0c-19.7 0-38.3 5.1-54.4 14.1zM448 96a64 64 0 1 0 128 0A64 64 0 1 0 448 96zM576 261.7c10 11.3 16 26.1 16 42.3s-6 31-16 42.3l0-84.7z"/><path d="M384 96A64 64 0 1 0 256 96a64 64 0 1 0 128 0zM256 261.7l0 84.7c-10-11.3-16-26.1-16-42.3s6-31.1 16-42.3zm128 84.7l0-84.7c10 11.3 16 26.1 16 42.3s-6 31-16 42.3zm0 58.9c37.8-18 64-56.5 64-101.2c0-61.9-50.1-112-112-112l-32 0c-61.9 0-112 50.1-112 112c0 44.7 26.2 83.2 64 101.2l0 42.8c0 17.7 14.3 32 32 32l64 0c17.7 0 32-14.3 32-32l0-42.8z"/></svg>,
                href: '/community-redirect',
                onClick: () => {
                    mixpanel.track('Community Page Opened', () => ({
                        'Opened From': 'Menu'
                    }));
                },
                isSelected: currentPath.startsWith('/community') || currentPath.startsWith('/resources'),
                isDisabled: false
            }
        ];
    // need to be dependent on inner state, not state instances
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [careMapState.state, currentPath, isViewer, hasSideNav]);

    return (
        <>
            <IonTabBar className={`footer-md ${classes.tabBar}`}>
                {nav.filter(i => !i.isHidden).map(item => {
                    // NOTE: IonTabBar opinionated and hard to wrap IonTabButton in a component so doing inline
                    // tried using TabNavigationItem.isTabButton etc... but started running into issues with navigation (wasn't working) and click
                    // https://github.com/ionic-team/ionic-framework/issues/23088
                    return (
                        <IonTabButton
                            key={item.id}
                            disabled={item.isDisabled}
                            tab={item.id}
                            href={item.href}
                            onClick={item.onClick}
                            data-type={`${item.id}-nav-item`}
                            className={`${classes.navItem} ${item.isSelected ? classes.selected : undefined}`}
                        >
                            {item.icon}
                            <IonLabel>{item.label}</IonLabel>
                        </IonTabButton>
                    );
                })}
            </IonTabBar>

            {canRecord &&
                <RecordButton />
            }

            {intercomState.state.unreadCount > 0 &&
                <div className={classes.unreadBadge}>
                    <ReminderBadge size="small" />
                </div>
            }
        </>
    );
};
